/**
 * Public Scan Verification Page
 *
 * URL: /cert/[token]
 *
 * Displays a publicly-accessible verification page confirming that an
 * automated accessibility scan was performed on a website. Does not
 * overclaim compliance — this is proof of testing, not certification.
 *
 * Anyone with the token can view this page — it's intentionally public
 * for easy sharing and verification without requiring authentication.
 */

import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import type { ReactNode } from "react";

interface CertificateData {
  website: string;
  organization: string;
  issued_at: string;
  issued_at_display: string;
  score: number;
  confidence: string;
  pages_scanned: number;
  issues_total: number;
  severity_summary: {
    critical: number;
    serious: number;
    moderate: number;
    minor: number;
  };
  methodology_label: string;
  limitations_text: string;
  certificate_hash: string;
  integrity_verified: boolean;
}

export default function CertificateVerificationPage() {
  const { token } = useParams<{ token: string }>();
  const [cert, setCert] = useState<CertificateData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setError("No verification token provided");
      setLoading(false);
      return;
    }

    const fetchCertificate = async () => {
      try {
        const response = await fetch(`/cert/public/${token}`);
        if (!response.ok) {
          if (response.status === 404) {
            setError("Verification record not found");
          } else {
            setError("Failed to load verification data");
          }
          return;
        }
        const data = await response.json();
        if (!data?.website || !data?.severity_summary) {
          setError("Invalid verification data");
          return;
        }
        setCert(data);
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : "Failed to load verification data",
        );
      } finally {
        setLoading(false);
      }
    };

    fetchCertificate();
  }, [token]);

  if (loading) {
    return (
      <div className="public-statement">
        <div className="public-statement__inner">
          <div className="loading-text">Loading verification data…</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="public-statement">
        <div className="public-statement__inner">
          <h1 className="public-statement__title">
            Verification not available
          </h1>
          <p className="text-muted">{error}</p>
          <p className="text-muted text-sm" style={{ marginTop: "0.5rem" }}>
            Please check your verification link and try again.
          </p>
        </div>
      </div>
    );
  }

  if (!cert) {
    return null;
  }

  return (
    <div className="public-statement">
      <div className="public-statement__inner">
        {/* Header */}
        <header className="public-statement__header">
          <h1 className="public-statement__title">
            Accessibility Scan Verification
          </h1>
          <p className="text-muted">
            This page confirms that an automated accessibility scan was
            performed on the website below. It is not a legal certification and
            does not imply full conformance with WCAG standards.
          </p>
          <IntegrityBadge verified={cert.integrity_verified} />
        </header>

        {/* Website info */}
        <section
          className="public-statement__meta"
          aria-label="Website information"
        >
          <div className="stat-grid">
            <div className="stat-item">
              <span className="stat-item__value">{cert.website}</span>
              <span className="stat-item__label">Website</span>
            </div>
            <div className="stat-item">
              <span className="stat-item__value">{cert.organization}</span>
              <span className="stat-item__label">Organisation</span>
            </div>
            <div className="stat-item">
              <span className="stat-item__value">{cert.issued_at_display}</span>
              <span className="stat-item__label">Scan date</span>
            </div>
          </div>
        </section>

        {/* Score section */}
        <CertSection title="Automated scan score">
          <div className="stat-grid">
            <div className="stat-item">
              <span className="stat-item__value">{cert.score}/100</span>
              <span className="stat-item__label">Score</span>
            </div>
            <div className="stat-item">
              <span className="stat-item__value">{cert.confidence}</span>
              <span className="stat-item__label">Confidence</span>
            </div>
            <div className="stat-item">
              <span className="stat-item__value">{cert.pages_scanned}</span>
              <span className="stat-item__label">Pages scanned</span>
            </div>
            <div className="stat-item">
              <span className="stat-item__value">{cert.issues_total}</span>
              <span className="stat-item__label">Total issues</span>
            </div>
          </div>
          <p className="text-muted text-sm" style={{ marginTop: "0.5rem" }}>
            This score is based on automated scanning tools only. It does not
            reflect manual accessibility testing.
          </p>
        </CertSection>

        {/* Severity breakdown */}
        <CertSection title="Issue severity breakdown">
          <div className="stat-grid">
            <SeverityItem
              label="Critical"
              count={cert.severity_summary.critical}
              color="var(--color-danger, #dc3545)"
            />
            <SeverityItem
              label="Serious"
              count={cert.severity_summary.serious}
              color="var(--color-warning, #fd7e14)"
            />
            <SeverityItem
              label="Moderate"
              count={cert.severity_summary.moderate}
              color="var(--color-caution, #ffc107)"
            />
            <SeverityItem
              label="Minor"
              count={cert.severity_summary.minor}
              color="var(--color-info, #0d6efd)"
            />
          </div>
        </CertSection>

        {/* Methodology */}
        <CertSection title="Testing methodology">
          <p className="text-muted text-sm">{cert.methodology_label}</p>
        </CertSection>

        {/* Limitations */}
        <CertSection title="Important limitations">
          <div className="alert alert--info" style={{ whiteSpace: "pre-wrap" }}>
            {cert.limitations_text}
          </div>
        </CertSection>

        {/* Verification hashes */}
        <CertSection title="Verification details">
          <div
            style={{ display: "flex", flexDirection: "column", gap: "1rem" }}
          >
            <div>
              <p
                className="text-muted text-sm"
                style={{ fontWeight: 600, marginBottom: "0.25rem" }}
              >
                RECORD HASH (SHA-256)
              </p>
              <code
                className="code-block"
                style={{
                  display: "block",
                  background: "var(--color-bg-muted, #f3f4f6)",
                  padding: "0.75rem",
                  borderRadius: "4px",
                  fontSize: "0.75rem",
                  overflowX: "auto",
                  wordBreak: "break-all",
                }}
              >
                {cert.certificate_hash}
              </code>
            </div>
            <div>
              <p
                className="text-muted text-sm"
                style={{ fontWeight: 600, marginBottom: "0.25rem" }}
              >
                VERIFICATION TOKEN
              </p>
              <code
                className="code-block"
                style={{
                  display: "block",
                  background: "var(--color-bg-muted, #f3f4f6)",
                  padding: "0.75rem",
                  borderRadius: "4px",
                  fontSize: "0.75rem",
                  overflowX: "auto",
                  wordBreak: "break-all",
                }}
              >
                {token}
              </code>
            </div>
          </div>
        </CertSection>

        {/* Footer */}
        <footer className="public-statement__footer">
          <p className="text-muted text-sm" style={{ marginBottom: "0.5rem" }}>
            This verification record confirms that an automated accessibility
            scan was performed. It does not constitute a legal certification or
            declaration of WCAG conformance. Results reflect the state of the
            website at the time of scanning and may not reflect its current
            state.
          </p>
          <p className="public-statement__badge">
            AccessiMonitor — Automated Accessibility Scanning
          </p>
        </footer>
      </div>
    </div>
  );
}

function IntegrityBadge({ verified }: { verified: boolean }) {
  const style: React.CSSProperties = {
    marginTop: "1rem",
    padding: "0.75rem 1rem",
    borderRadius: "6px",
    fontSize: "0.875rem",
    fontWeight: 500,
    border: "1px solid",
    borderColor: verified
      ? "var(--color-success, #198754)"
      : "var(--color-warning, #ffc107)",
    background: verified
      ? "var(--color-success-bg, #d1e7dd)"
      : "var(--color-warning-bg, #fff3cd)",
    color: verified
      ? "var(--color-success-text, #0f5132)"
      : "var(--color-warning-text, #664d03)",
  };

  return (
    <div style={style}>
      {verified
        ? "Record integrity verified — data has not been modified since issuance."
        : "Record integrity could not be verified."}
    </div>
  );
}

function CertSection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <section style={{ marginBottom: "1.5rem" }}>
      <h2 className="section-title">{title}</h2>
      <div>{children}</div>
    </section>
  );
}

function SeverityItem({
  label,
  count,
  color,
}: {
  label: string;
  count: number;
  color: string;
}) {
  return (
    <div className="stat-item">
      <span className="stat-item__value" style={{ color }}>
        {count}
      </span>
      <span className="stat-item__label">{label}</span>
    </div>
  );
}
